import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from './Layout';
import AppIcon from './AppIcon';
import { CONTACT_EMAIL } from '../data/apps';
import {
    Alert,
    Apple,
    ArrowLeft,
    Check,
    ChevronRight,
    Clock,
    Info,
    Mail,
    ShieldCheck,
} from './Icons';

const slugify = text =>
    text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');

const GLANCE_ICON = { yes: Check, info: Info, care: Alert };

function Glance({ items }) {
    return (
        <div className="glance">
            {items.map(item => {
                const Icon = GLANCE_ICON[item.tone] || Info;
                return (
                    <div className={`glance-item tone-${item.tone}`} key={item.title}>
                        <Icon />
                        <div>
                            <strong>{item.title}</strong>
                            <span>{item.note}</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function Callout({ tone = 'warn', title, body }) {
    const Icon = tone === 'good' ? ShieldCheck : Alert;
    return (
        <div className={`callout${tone === 'good' ? ' tone-good' : ''}`}>
            <Icon />
            <div>
                <strong>{title}</strong>
                <p>{body}</p>
            </div>
        </div>
    );
}

function Section({ section, index, id }) {
    return (
        <section className="policy-section" id={id}>
            <h3>
                <span className="num">{index}</span>
                <span>{section.title}</span>
            </h3>

            <div className="policy-body">
                {section.lead && (
                    <p>
                        <strong>{section.lead}</strong>
                    </p>
                )}
                {section.body && <p>{section.body}</p>}

                {section.subs?.map(sub => (
                    <div className="sub" key={sub.title}>
                        <h4>{sub.title}</h4>
                        {sub.body && <p>{sub.body}</p>}
                        {sub.list && (
                            <ul>
                                {sub.list.map(entry => (
                                    <li key={entry}>{entry}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                ))}

                {section.callout && <Callout {...section.callout} />}
            </div>
        </section>
    );
}

/** Sticky table of contents that highlights the section currently in view. */
function Toc({ entries }) {
    const [active, setActive] = useState(entries[0]?.id);
    const ratios = useRef(new Map());

    useEffect(() => {
        const nodes = entries
            .map(entry => document.getElementById(entry.id))
            .filter(Boolean);
        if (!nodes.length) return undefined;

        const observer = new IntersectionObserver(
            observed => {
                observed.forEach(entry => {
                    ratios.current.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
                });

                // Pick the most-visible section; ties resolve to document order.
                let best = null;
                let bestRatio = 0;
                entries.forEach(entry => {
                    const ratio = ratios.current.get(entry.id) || 0;
                    if (ratio > bestRatio) {
                        best = entry.id;
                        bestRatio = ratio;
                    }
                });
                if (best) setActive(best);
            },
            { rootMargin: '-88px 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] }
        );

        nodes.forEach(node => observer.observe(node));
        return () => observer.disconnect();
    }, [entries]);

    return (
        <nav className="toc" aria-label="Privacy policy sections">
            <div className="toc-label">On this page</div>
            <ol>
                {entries.map(entry => (
                    <li key={entry.id}>
                        <a href={`#${entry.id}`} aria-current={active === entry.id ? 'true' : undefined}>
                            {entry.title}
                        </a>
                    </li>
                ))}
            </ol>
        </nav>
    );
}

/**
 * Renders a full support page for one app: header, contact card, and (when the
 * app has one) its privacy policy with a sticky table of contents.
 */
export default function SupportPage({ app }) {
    const { policy } = app;

    const entries = useMemo(
        () =>
            (policy?.sections || []).map((section, i) => ({
                id: `${slugify(section.title)}-${i + 1}`,
                title: section.title,
            })),
        [policy]
    );

    useEffect(() => {
        document.title = `${app.name} Support`;
    }, [app.name]);

    return (
        <Layout>
            <nav className="crumbs" aria-label="Breadcrumb">
                <Link to="/">
                    <ArrowLeft />
                    All apps
                </Link>
                <ChevronRight />
                <span>{app.name}</span>
            </nav>

            <div className="card card-glow app-hero rise">
                <AppIcon app={app} />
                <div className="app-hero-text">
                    <h1>{app.name}</h1>
                    {app.tagline && <p className="app-hero-tagline">{app.tagline}</p>}

                    <div className="app-hero-meta">
                        <span className={`pill ${app.status === 'live' ? 'pill-live' : 'pill-soon'}`}>
                            {app.status === 'live' ? 'Available now' : 'Coming soon'}
                        </span>
                        <span className="pill pill-neutral">
                            <Apple width="12" height="12" />
                            {app.platform}
                        </span>
                        {app.store && (
                            <>
                                <span className="pill pill-neutral">{app.store.price}</span>
                                <span className="pill pill-neutral">Version {app.store.version}</span>
                            </>
                        )}
                        {policy && (
                            <span className="pill pill-info">
                                <ShieldCheck />
                                Privacy policy below
                            </span>
                        )}
                    </div>

                    {app.store && (
                        <div className="app-hero-actions">
                            <a
                                className="btn btn-primary"
                                href={app.store.url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Apple width="15" height="15" />
                                View on the App Store
                            </a>
                        </div>
                    )}
                </div>
            </div>

            {app.summary && (
                <section className="card about">
                    <h2>What it does</h2>
                    <p>{app.summary}</p>
                </section>
            )}

            <div className="card contact">
                <span className="contact-icon">
                    <Mail />
                </span>
                <div className="contact-text">
                    <strong>Run into a problem?</strong>
                    <p>Email us and we’ll get back to you. Questions, bugs and suggestions all welcome.</p>
                </div>
                <a className="btn btn-primary" href={`mailto:${CONTACT_EMAIL}`}>
                    <Mail />
                    {CONTACT_EMAIL}
                </a>
            </div>

            {policy ? (
                <div className="policy-layout">
                    <Toc entries={entries} />

                    <div>
                        <div className="policy-head">
                            <h2>Privacy Policy</h2>
                            <span className="stamp">
                                <Clock />
                                Last updated {policy.updated}
                            </span>
                        </div>

                        <p className="policy-intro">{policy.intro}</p>

                        {policy.glance && <Glance items={policy.glance} />}

                        {policy.sections.map((section, i) => (
                            <Section
                                key={entries[i].id}
                                id={entries[i].id}
                                index={i + 1}
                                section={section}
                            />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="card contact" style={{ marginTop: '2.5rem' }}>
                    <span className="contact-icon">
                        <Clock />
                    </span>
                    <div className="contact-text">
                        <strong>Not released yet</strong>
                        <p>
                            {app.name} is still in development. A privacy policy will be published here
                            alongside the first release.
                        </p>
                    </div>
                </div>
            )}
        </Layout>
    );
}
